import React, { useState } from 'react';
import { Alert, Button } from 'reactstrap';

// Comprehend brand green, applied inline (not a Bootstrap theme color).
const GREEN = '#1d9e75';

const primaryButtonStyle = {
  borderRadius: 12,
  fontSize: 'clamp(12px, 4vw, 15px)',
  fontWeight: 700,
  lineHeight: 1.2,
  minHeight: 44,
  padding: '8px 6px',
  whiteSpace: 'nowrap',
};

const reviewSummaryStyle = {
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.45,
};

const chipStyle = {
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.15,
  maxWidth: '100%',
  minHeight: 25,
  padding: '4px 9px',
};

/**
 * Review step ("Here's what I heard") for the Instruction Assistant.
 *
 * Shows one bullet per planned change — a plain-language summary with the sections
 * it touches rendered as chips. The clinician can discard a whole change (the × / ↩
 * on the row) or drop individual sections (tap a chip). Apply persists only what's
 * kept.
 *
 * `status` is the state of the plan request: 'done' | 'error'.
 * When 'done', `changes` holds `[{ summary, directive, sections: [name, ...] }]`.
 * The apply actions receive `keptChanges` — the kept changes, each narrowed to its
 * kept sections (shape `[{ summary, directive, sections }]`), matching the
 * generateInstructionRevisions contract. `summary` is carried through only so the
 * bullet the clinician saw is recorded with the change; the backend revises
 * instructions from `directive`.
 */
const InstructionReview = ({
  status,
  changes,
  error,
  saving,
  noteSaveReady,
  onSaveTemplate,
  onSaveTemplateAndNote,
  onReject,
  onBack,
  onRetry,
}) => {
  // Whole changes the clinician discarded: { [changeIndex]: true }.
  const [removedChanges, setRemovedChanges] = useState({});
  // Individual sections dropped from a change: { [`${changeIndex}:${section}`]: true }.
  const [removedSections, setRemovedSections] = useState({});

  if (status === 'error') {
    return (
      <div className="d-flex flex-column">
        <Alert color="warning" className="text-center" style={{ fontSize: 15 }}>
          {error || 'Something went wrong. Please try again.'}
        </Alert>
        <div>
          <Button
            type="button"
            color="success"
            block
            className="font-weight-bold shadow-sm"
            style={{ ...primaryButtonStyle, backgroundColor: GREEN, borderColor: GREEN }}
            onClick={onRetry}
          >
            Try again
          </Button>
          <div className="text-center mt-2">
            <Button type="button" color="link" size="sm" onClick={onBack}>
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const changeList = changes || [];

  if (changeList.length === 0) {
    return (
      <div className="d-flex flex-column">
        <Alert color="info" className="text-center" style={{ fontSize: 15 }}>
          No changes were needed — your current preferences already cover that.
        </Alert>
        <div>
          <Button
            type="button"
            color="success"
            block
            className="font-weight-bold shadow-sm"
            style={{ ...primaryButtonStyle, backgroundColor: GREEN, borderColor: GREEN }}
            onClick={onBack}
          >
            Back
          </Button>
          <div className="text-center mt-2">
            <Button type="button" color="link" size="sm" onClick={onReject}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const sectionKey = (i, section) => `${i}:${section}`;
  const isSectionRemoved = (i, section) =>
    !!removedSections[sectionKey(i, section)];
  const keptSectionsOf = (change, i) =>
    (change.sections || []).filter((s) => !isSectionRemoved(i, s));

  // The kept changes, each narrowed to its kept sections — dropping any change the
  // clinician discarded or that has no sections left.
  const keptChanges = changeList
    .map((change, i) => ({
      summary: change.summary,
      directive: change.directive,
      sections: removedChanges[i] ? [] : keptSectionsOf(change, i),
    }))
    .filter((c) => c.sections.length > 0);
  const hasKept = keptChanges.length > 0;

  const toggleChange = (i) =>
    setRemovedChanges((prev) => ({ ...prev, [i]: !prev[i] }));

  const toggleSection = (i, section) =>
    setRemovedSections((prev) => {
      const key = sectionKey(i, section);
      return { ...prev, [key]: !prev[key] };
    });

  return (
    <div>
      <p className="mb-2 text-muted" style={{ fontSize: 14, lineHeight: 1.45 }}>
        Keep what fits — remove the rest.
      </p>
      <div>
        {changeList.map((change, i) => {
          const removed = !!removedChanges[i];
          const sections = change.sections || [];
          const isLast = i === changeList.length - 1;
          return (
            <div
              key={i}
              className={`py-2${isLast ? '' : ' border-bottom'}`}
            >
              <div className="d-flex align-items-start">
                <i
                  className="fa fa-circle mr-3 flex-shrink-0"
                  style={{
                    color: removed ? '#c7c7c7' : GREEN,
                    fontSize: 6,
                    marginTop: 9,
                  }}
                  aria-hidden="true"
                />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  {removed ? (
                    <del
                      className="text-muted text-break"
                      style={reviewSummaryStyle}
                    >
                      {change.summary || 'Updated instruction'}
                    </del>
                  ) : (
                    <span
                      className="text-break"
                      style={reviewSummaryStyle}
                    >
                      {change.summary || 'Updated instruction'}
                    </span>
                  )}
                  {sections.length > 0 && (
                    <div className="mt-2">
                      {sections.map((s) => {
                        const off = removed || isSectionRemoved(i, s);
                        return (
                          <Button
                            key={s}
                            type="button"
                            size="sm"
                            color="secondary"
                            outline
                            className="rounded-pill mr-2 mb-2 d-inline-flex align-items-center"
                            style={{
                              ...chipStyle,
                              ...(off
                                ? {}
                                : { borderColor: GREEN, color: GREEN }),
                            }}
                            onClick={() => toggleSection(i, s)}
                            disabled={removed}
                            aria-pressed={!off}
                            aria-label={off ? `Restore ${s}` : `Remove ${s}`}
                          >
                            <span
                              className="d-inline-block text-truncate align-middle"
                              style={{ maxWidth: 180 }}
                              title={s}
                            >
                              {off ? <del>{s}</del> : s}
                            </span>
                            <i
                              className={
                                off
                                  ? 'fa fa-plus ml-2'
                                  : 'fa fa-times ml-2'
                              }
                              style={{
                                fontSize: 10,
                                lineHeight: 1,
                                opacity: 0.65,
                              }}
                              aria-hidden="true"
                            />
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="ml-2 flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                  style={{
                    borderRadius: 8,
                    color: removed ? GREEN : '#8a8a8a',
                    fontSize: 14,
                    height: 28,
                    lineHeight: 1,
                    padding: 0,
                    width: 28,
                  }}
                  onClick={() => toggleChange(i)}
                  aria-label={
                    removed ? 'Keep this change' : 'Discard this change'
                  }
                >
                  <i
                    className={removed ? 'fa fa-undo' : 'fa fa-times'}
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3">
        {noteSaveReady && (
          <Button
            type="button"
            color="success"
            block
            className="font-weight-bold shadow-sm"
            style={{ ...primaryButtonStyle, backgroundColor: GREEN, borderColor: GREEN }}
            onClick={() => onSaveTemplateAndNote(keptChanges)}
            disabled={saving || !hasKept}
          >
            Update Note &amp; Save Preference
          </Button>
        )}
        <Button
          type="button"
          color="success"
          outline={noteSaveReady}
          block
          className={`font-weight-bold${noteSaveReady ? '' : ' shadow-sm'}`}
          style={{
            ...primaryButtonStyle,
            ...(noteSaveReady
              ? {
                  backgroundColor: 'transparent',
                  borderColor: GREEN,
                  boxShadow: 'none',
                  color: GREEN,
                }
              : { backgroundColor: GREEN, borderColor: GREEN }),
            opacity: saving || !hasKept ? 0.55 : 1,
          }}
          onClick={() => onSaveTemplate(keptChanges)}
          disabled={saving || !hasKept}
        >
          Save Preference for Future Notes
        </Button>
        <div className="text-center mt-2">
          <Button
            type="button"
            color="link"
            size="sm"
            className="text-muted"
            onClick={onReject}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstructionReview;
